// 場景背景 — 對應 Unity LandscapeUI.prefab 的 MGBackground/FGBackground/Background_Front：
// 三者都是 ani_bg 骨骼（SkeletonGraphic 2526.38×2522.17、畫面置中），MG 常駐播 `mg` loop、FG 播 `fg` loop，
// Background_Front 平時隱藏、野狼閒置觸發時播一次 `mg_Woif_L`（GamePlayUI.PlayWolf）。
// 進出 FG 的切換是「水平滑動」：FG 由 +2526.383 滑入、MG 滑出到 −2526.383（GamePlayUI.ToFg/ToMg 的
// _backgroundStart/EndPosition，_tweenBackgroundDuration=0.5s）。
// 骨骼載入失敗時 fallback 回靜態 bg_mg/bg_fg 圖（cover 裁切），行為與先前版本相同。

import { Container, Sprite } from 'pixi.js'
import type { Spine } from '@esotericsoftware/spine-pixi-v8'
import type { Orientation, Resizable } from '../../framework/render/design'
import { unityScaleLandscape } from '../../framework/render/design'
import { getBackgroundTexture } from '../assets/BackgroundTextures'
import { animationDuration, createEffectSpine, EFFECT_ANIM, hasAnimation } from '../assets/EffectSpines'
import { killTweensOf, tweenTo } from './tween'

/** 背景滑動切換時長（秒），對齊 Unity _tweenBackgroundDuration。 */
const SLIDE_DURATION = 0.5
/** Unity FGBackground 起始 x／MGBackground 滑出 x 的絕對值（設計 px，_backgroundStart/EndPosition）。 */
const SLIDE_OFFSET = 2526.383
/** ani_bg 骨骼的設計尺寸（MGBackground RectTransform sizeDelta）。 */
const BG_DESIGN_W = 2526.383
const BG_DESIGN_H = 2522.169
/**
 * 靜態 fallback 的垂直裁切錨點（0=頂對齊、0.5=置中）。背景原圖為方形（1260×1260），橫版 cover 需裁上下；
 * 對照 Unity 實機呈現取「偏上」段（頂緣露出紫幕流蘇、底緣到舞台前緣，森林窗景置中）。
 */
const BG_VERTICAL_ANCHOR = 0.42

export class BackgroundView extends Container implements Resizable {
  // 靜態 fallback（骨骼可用時隱藏）。
  private readonly mainSprite = new Sprite(getBackgroundTexture('mg'))
  private readonly freeGameSprite = new Sprite(getBackgroundTexture('fg'))

  // ani_bg 骨骼三份實例（MG／FG／野狼前景）。載入失敗為 null → 走靜態 fallback。
  private mainSpine: Spine | null = null
  private freeGameSpine: Spine | null = null
  private wolfSpine: Spine | null = null

  private screenW = 0
  private screenH = 0
  private orientation: Orientation = 'landscape'
  /** 滑動進度：0=主遊戲背景在畫面中央，1=FG 背景在畫面中央。 */
  private readonly slideProxy = { value: 0 }

  constructor() {
    super()
    this.addChild(this.mainSprite, this.freeGameSprite)
  }

  /**
   * 建立 ani_bg 骨骼（須於 loadEffectSpines() 完成後呼叫一次）。
   * 對應 MGBackground(mg loop)／FGBackground(fg loop)／Background_Front(野狼、平時隱藏)。
   */
  initSpine(): void {
    if (this.mainSpine !== null) return
    const mg = createEffectSpine('bg')
    const fg = createEffectSpine('bg')
    const wolf = createEffectSpine('bg')
    if (mg === null || fg === null || wolf === null) return

    mg.state.setAnimation(0, EFFECT_ANIM.bgMain, true)
    fg.state.setAnimation(0, EFFECT_ANIM.bgFreeGame, true)
    wolf.visible = false

    this.mainSpine = mg
    this.freeGameSpine = fg
    this.wolfSpine = wolf
    // 野狼前景蓋在 MG 背景上（Background_Front 是 MGBackground 子節點）。
    this.addChild(mg, fg, wolf)
    this.mainSprite.visible = false
    this.freeGameSprite.visible = false
    this.applyPositions()
  }

  /**
   * 野狼探頭一次（對應 GamePlayUI.PlayWolf：Background_Front 播 mg_Woif_L 後隱藏）。
   * 直版若骨骼含 mg_Woif_P 則用直版變體。骨骼不可用時靜默略過（僅音效由 AudioManager 播）。
   */
  async playWolf(): Promise<void> {
    const spine = this.wolfSpine
    if (spine === null || this.slideProxy.value > 0) return
    const portraitName = EFFECT_ANIM.bgWolfPortrait
    const name =
      this.orientation === 'portrait' && hasAnimation(spine, portraitName) ? portraitName : EFFECT_ANIM.bgWolf
    if (!hasAnimation(spine, name)) return
    spine.visible = true
    spine.state.setAnimation(0, name, false)
    await new Promise((resolve) => setTimeout(resolve, animationDuration(spine, name, 2) * 1000))
    spine.visible = false
  }

  /** 滑入 FG 背景（主遊戲背景往左滑出、FG 背景由右滑入）。 */
  async transitionToFreeGame(): Promise<void> {
    await this.slideTo(1)
  }

  /** 滑回主遊戲背景。 */
  async transitionToMainGame(): Promise<void> {
    await this.slideTo(0)
  }

  /** 立即設定背景（不動畫），供開機初始化。 */
  setImmediate(freeGame: boolean): void {
    killTweensOf(this.slideProxy)
    this.slideProxy.value = freeGame ? 1 : 0
    this.applyPositions()
  }

  private async slideTo(target: number): Promise<void> {
    killTweensOf(this.slideProxy)
    if (this.wolfSpine !== null) this.wolfSpine.visible = false
    await tweenTo(this.slideProxy, {
      value: target,
      duration: SLIDE_DURATION,
      ease: 'none',
      onUpdate: () => this.applyPositions(),
    })
    this.applyPositions()
  }

  resize(width: number, height: number, orientation: Orientation): void {
    this.screenW = width
    this.screenH = height
    this.orientation = orientation
    for (const sprite of [this.mainSprite, this.freeGameSprite]) {
      const scale = Math.max(width / sprite.texture.width, height / sprite.texture.height)
      sprite.scale.set(scale)
    }
    this.applyPositions()
  }

  /**
   * 骨骼縮放：橫版以 Unity Canvas 等比（骨骼四邊本就超出 1920×1080 供裁切），
   * 但保底 cover（極端長寬比不露底）；直版沿用 cover 保底（直版權威值待 PoriraitUI 對齊）。
   */
  private spineScale(): number {
    return Math.max(
      unityScaleLandscape(this.screenW, this.screenH),
      this.screenW / BG_DESIGN_W,
      this.screenH / BG_DESIGN_H,
    )
  }

  /** 依 slide 進度水平定位 MG/FG 背景（骨骼與靜態 fallback 各自套用）。 */
  private applyPositions(): void {
    const slide = this.slideProxy.value
    const cx = this.screenW / 2
    const cy = this.screenH / 2

    if (this.mainSpine !== null && this.freeGameSpine !== null && this.wolfSpine !== null) {
      const s = this.spineScale()
      const offset = SLIDE_OFFSET * s
      this.mainSpine.scale.set(s)
      this.freeGameSpine.scale.set(s)
      this.wolfSpine.scale.set(s)
      this.mainSpine.position.set(cx - slide * offset, cy)
      this.freeGameSpine.position.set(cx + (1 - slide) * offset, cy)
      this.wolfSpine.position.set(this.mainSpine.position.x, cy)
      this.mainSpine.visible = slide < 1
      this.freeGameSpine.visible = slide > 0
      return
    }

    const w = this.screenW
    const mainBaseX = (w - this.mainSprite.width) / 2
    const fgBaseX = (w - this.freeGameSprite.width) / 2
    const mainBaseY = (this.screenH - this.mainSprite.height) * BG_VERTICAL_ANCHOR
    const fgBaseY = (this.screenH - this.freeGameSprite.height) * BG_VERTICAL_ANCHOR

    this.mainSprite.position.set(mainBaseX - slide * w, mainBaseY)
    this.freeGameSprite.position.set(fgBaseX + (1 - slide) * w, fgBaseY)
    // 完全滑出的背景可隱藏，省繪製；中途皆顯示以呈現滑動。
    this.mainSprite.visible = slide < 1
    this.freeGameSprite.visible = slide > 0
  }
}
